import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronRight, Clock, AlertCircle, CalendarDays, ShieldCheck, BadgeCheck, Lock, Sparkles } from "lucide-react";
import { Screen, ScrollArea } from "@/components/jk/ui";
import { Avatar, Card, BottomNav, SectionLabel, FullScreenLoader, Pill } from "@/components/jk/kit";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";
import { greeting, formatShortMoney, formatDate, formatTime } from "@/lib/format";
import api from "@/lib/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const { astrologer } = useAuth();
  const { open: openMenu } = useMenu();
  const [data, setData] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/dashboard"), api.get("/astrologer/paywall")])
      .then(([d, p]) => { setData(d.data); setPaywall(p.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/astrologer/kyc").then(({ data }) => setKyc(data)).catch(() => {});
  }, []);

  if (loading) return <FullScreenLoader />;

  const firstName = (astrologer?.full_name || "").split(" ")[0] || "there";
  const stats = data?.stats || { this_month: 0, consults: 0, new: 0 };
  const next = data?.next_consultation;
  const schedule = data?.today_schedule || [];
  const upcoming = data?.upcoming_bookings || [];
  const freeTier = data?.free_tier;
  const showPaywall = paywall?.pending_booking && paywall?.subscription_status !== "active";

  return (
    <Screen testid="dashboard-screen">
      <header className="flex shrink-0 items-center justify-between px-6 pb-2 pt-8">
        <div>
          <p className="text-[13px] text-[#666666]">{greeting()},</p>
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#111111]" data-testid="dash-greeting-name">{firstName}</h1>
          {kyc?.payout_eligible && (
            <button onClick={() => navigate("/kyc")} data-testid="verified-tag" className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#eaf6ee] px-2.5 py-1 text-[12px] font-semibold text-[#1f9254] active:scale-95">
              <BadgeCheck size={13} strokeWidth={2.25} /> Verified
            </button>
          )}
        </div>
        <button onClick={openMenu} data-testid="dash-avatar" aria-label="Open menu">
          <Avatar name={astrologer?.full_name} photo={astrologer?.photo_url} size={44} />
        </button>
      </header>

      <ScrollArea className="px-6 pt-4">
        {kyc && !kyc.payout_eligible && (() => {
          const nameMismatch = kyc.kyc_complete && !kyc.name_match;
          return (
            <button onClick={() => navigate("/kyc")} data-testid="kyc-block" className="mb-4 flex w-full items-center gap-3 rounded-[16px] border border-[#e9e6e1] bg-white px-4 py-3.5 text-left active:scale-[0.99]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fdf2e6] text-[#b5651d]">
                {nameMismatch ? <AlertCircle size={16} strokeWidth={1.75} /> : <ShieldCheck size={16} strokeWidth={1.75} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-medium text-[#111111]">Identity &amp; payouts (KYC)</p>
                <p className="text-[12px] text-[#666666]">
                  {nameMismatch ? "Name mismatch — update your profile name to match your PAN" : "Verify Aadhaar & PAN to enable payouts"}
                </p>
              </div>
              <Pill tone="peach" data-testid="kyc-block-status">
                {nameMismatch ? <><AlertCircle size={12} className="mr-1" /> Action needed</> : <><Lock size={12} className="mr-1" /> On hold</>}
              </Pill>
            </button>
          );
        })()}

        {showPaywall && (
          <button onClick={() => navigate("/paywall")} data-testid="paywall-banner" className="mb-4 flex w-full items-center gap-3 rounded-[14px] bg-[var(--jk-peach)] p-4 text-left active:scale-[0.99]">
            <AlertCircle size={20} strokeWidth={1.75} className="shrink-0 text-[#111111]" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-[#111111]">New booking needs your action</p>
              <p className="text-[12px] text-[#6b5b4e]">Subscribe to accept your 6th client &middot; tap to review</p>
            </div>
            <ArrowRight size={16} className="text-[#111111]" />
          </button>
        )}

        {/* Next consultation */}
        <SectionLabel className="mb-3">Next consultation</SectionLabel>

        {next ? (
          <Card dark data-testid="next-consultation">
            <div className="flex items-center justify-between">
              <Pill tone="light">{next.service_name}</Pill>
              <span className="flex items-center gap-1 text-[12px] text-white/70"><Clock size={13} /> {formatTime(next.booking_time)}</span>
            </div>
            <h2 className="mt-4 text-[22px] font-bold tracking-[-0.01em]">{next.client_name}</h2>
            <p className="mt-1 text-[13px] text-white/60">{formatDate(next.booking_date)}</p>
            <button onClick={() => navigate(`/brief/${next.id}`)} data-testid="view-brief" className="mt-5 flex w-full items-center justify-center gap-2 rounded-[12px] bg-white py-3 text-[14px] font-semibold text-[#111111] active:scale-[0.98]">
              View Client Brief <ArrowUpRight size={16} strokeWidth={2} />
            </button>
          </Card>
        ) : (
          <Card data-testid="next-consultation-empty" className="flex flex-col items-center py-8 text-center">
            <CalendarDays size={26} strokeWidth={1.5} className="text-[#cfccc6]" />
            <p className="mt-3 text-[14px] font-medium text-[#111111]">No upcoming consultations</p>
            <p className="mt-1 text-[12px] text-[#666666]">Share your booking link to get started.</p>
            {freeTier?.is_free && (
              <div className="mt-4 flex flex-col items-center gap-1.5" data-testid="free-tier-indicator">
                <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5" style={{ backgroundColor: "#FFF3E0" }}>
                  <Sparkles size={11} strokeWidth={2} style={{ color: "#E8572A", flexShrink: 0 }} />
                  {Array.from({ length: freeTier.limit }, (_, i) => (
                    <span key={i} className="h-2 w-2 rounded-full" style={{ backgroundColor: i < freeTier.unique_clients_used ? "#E8572A" : "#F5C4B0" }} />
                  ))}
                  {freeTier.unique_clients_used >= freeTier.limit ? (
                    <button onClick={() => navigate("/paywall")} data-testid="free-tier-upgrade-link" className="text-[11px] font-semibold underline underline-offset-2" style={{ color: "#E8572A" }}>
                      Upgrade to keep accepting clients
                    </button>
                  ) : (
                    <span className="text-[11px] font-medium" style={{ color: "#E8572A" }} data-testid="free-tier-label">
                      {freeTier.unique_clients_used} of {freeTier.limit} free clients used
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400">Your first 5 paying clients are free - no subscription needed.</p>
              </div>
            )}
          </Card>
        )}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-3 gap-3" data-testid="dash-stats">
          <div className="rounded-[14px] bg-[var(--jk-peach)] p-3.5">
            <p className="text-[19px] font-bold leading-none text-[#111111]">{formatShortMoney(stats.this_month)}</p>
            <p className="mt-1.5 text-[11px] text-[#6b5b4e]">This month</p>
          </div>
          <div className="rounded-[14px] bg-[var(--jk-periwinkle)] p-3.5">
            <p className="text-[19px] font-bold leading-none text-[#111111]">{stats.consults}</p>
            <p className="mt-1.5 text-[11px] text-[#555]">Consults</p>
          </div>
          <div className="rounded-[14px] bg-[var(--jk-butter)] p-3.5">
            <p className="text-[19px] font-bold leading-none text-[#111111]">{stats.new}</p>
            <p className="mt-1.5 text-[11px] text-[#6b5d2a]">New clients</p>
          </div>
        </div>

        {/* Today's schedule */}
        <div className="mt-6 pb-6">
          <SectionLabel className="mb-3">Today&apos;s schedule</SectionLabel>
          {schedule.length === 0 ? (
            <p className="rounded-[14px] border border-dashed border-[#e0ddd7] py-6 text-center text-[13px] text-[#aaaaaa]" data-testid="schedule-empty">
              Nothing scheduled for today
            </p>
          ) : (
            <div className="space-y-2.5" data-testid="today-schedule">
              {schedule.map((b) => (
                <button key={b.id} onClick={() => navigate(`/brief/${b.id}`)} className="flex w-full items-center gap-3 rounded-[14px] border border-[#e9e6e1] bg-white p-3.5 text-left active:scale-[0.99]">
                  <div className="flex w-14 shrink-0 flex-col items-center rounded-[10px] bg-[#f2f0ec] py-1.5">
                    <span className="text-[13px] font-bold text-[#111111]">{formatTime(b.booking_time).split(" ")[0]}</span>
                    <span className="text-[10px] text-[#666666]">{formatTime(b.booking_time).split(" ")[1]}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold text-[#111111]">{b.client_name}</p>
                    <p className="truncate text-[12px] text-[#666666]">{b.service_name}</p>
                  </div>
                  <ArrowRight size={16} className="text-[#cfccc6]" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming this week */}
        {upcoming.length > 0 && (
          <div className="mt-2 pb-6">
            <SectionLabel className="mb-3">Upcoming this week</SectionLabel>
            <div className="space-y-2.5" data-testid="upcoming-bookings">
              {upcoming.map((b) => (
                <button key={b.id} onClick={() => navigate(`/brief/${b.id}`)} className="flex w-full items-center gap-3 rounded-[14px] border border-[#e9e6e1] bg-white p-3.5 text-left active:scale-[0.99]">
                  <div className="flex w-14 shrink-0 flex-col items-center rounded-[10px] bg-[#f2f0ec] py-1.5">
                    <span className="text-[11px] font-bold text-[#111111]">{formatDate(b.booking_date).split(" ")[0]}</span>
                    <span className="text-[10px] text-[#666666]">{formatDate(b.booking_date).split(" ")[1]}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold text-[#111111]">{b.client_name}</p>
                    <p className="truncate text-[12px] text-[#666666]">{b.service_name} · {formatTime(b.booking_time)}</p>
                  </div>
                  <ChevronRight size={16} className="text-[#cfccc6]" />
                </button>
              ))}
            </div>
          </div>
        )}
      </ScrollArea>

      <BottomNav />
    </Screen>
  );
};

export default Dashboard;
